import * as React from "react"
import { useRecoilValue } from "recoil"
import { brandingSelector } from "../../../recoil/atoms/room"
import { Wrapper, ErrorTextContainer } from "./fields.styled"
import { SelectFieldProps } from "./fields.interface"
import Select from "react-select"
import {
  replaceOpacity,
  getOpacity,
  isRGBAcolor,
  blendWithWhiteBackground
} from "../../../util"
import { useTranslation } from "react-i18next"

export const SelectField: React.FC<SelectFieldProps> = ({
  errorMessage,
  stylesWrapper,
  isError,
  selectedOption,
  onChange,
  options,
  disabled,
  customStyles,
  isSearchable,
  defaultValue
}): React.ReactElement => {
  const { t } = useTranslation()
  const roomStyles = useRecoilValue(brandingSelector)

  const borderColor = React.useMemo<string>(() => {
    if (isError) {
      return roomStyles?.servicePanelElementWarningText ?? "red"
    }
    if (isRGBAcolor(roomStyles?.servicePanelElementBorder)) {
      return getOpacity(roomStyles?.servicePanelElementBorder) > 0.3
        ? replaceOpacity(
            roomStyles?.servicePanelElementBorder,
            getOpacity(roomStyles?.servicePanelElementBorder) - 0.3
          )
        : replaceOpacity(roomStyles?.servicePanelElementBorder, 0.1)
    }
    return `${roomStyles?.servicePanelElementBorder}b3` ?? "inherit"
  }, [
    isError,
    roomStyles?.servicePanelElementBorder,
    roomStyles?.servicePanelElementWarningText
  ])

  const styles = {
    container: (provided, state) => ({
      ...provided,
      width: "100%",
      ...customStyles?.container
    }),
    control: (provided, state) => ({
      ...provided,
      ...customStyles?.control,
      boxShadow: "none",
      background: roomStyles.servicePanelElementBackground ?? "#e9ecef",
      border: roomStyles.servicePanelElementBorder
        ? `2px solid ${borderColor}`
        : "2px solid #ced4da",
      ":hover": {
        border: roomStyles.servicePanelElementBorder
          ? `2px solid ${roomStyles.servicePanelElementBorder}`
          : "2px solid #ced4da"
      },
      ":focus": {
        border: 0,
        boxShadow: "none"
      }
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      ...customStyles?.valueContainer
    }),
    placeholder: (provided, state) => ({
      ...provided,
      ...customStyles?.placeholder,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      opacity: 0.5
    }),
    option: (provided, state) => ({
      ...provided,
      ...customStyles?.option,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      opacity: state.isSelected ? 1 : 0.75,
      backgroundColor: "unset",
      cursor: "pointer",
      ":hover": {
        opacity: 1
      },
      ":active": {
        backgroundColor: "unset"
      }
    }),
    menuList: (provided, state) => ({
      ...provided,
      ...customStyles?.menuList,
      borderRadius: "0.3rem"
    }),
    menu: (provided, state) => ({
      ...provided,
      ...customStyles?.menu,
      background: blendWithWhiteBackground(roomStyles?.servicePanelElementModalBackground) ?? "#e9ecef",
      border: roomStyles.servicePanelElementBorder
        ? `2px solid ${roomStyles.servicePanelElementBorder}`
        : "2px solid #ced4da",
      borderRadius: "0.3rem"
    }),
    input: (provided, state) => ({
      ...provided,
      ...customStyles?.input,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      cursor: "pointer"
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      ...customStyles?.dropdownIndicator,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      opacity: state.isSelected ? 1 : 0.75,
      paddingLeft: "0rem",

      ":hover": {
        opacity: 1
      }
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      ...customStyles?.indicatorsContainer,
      cursor: "pointer"
    }),
    indicatorContainer: (provided, state) => ({
      ...provided,
      ...customStyles?.indicatorContainer
    }),
    indicatorSeparator: (provided, state) => ({
      ...provided,
      ...customStyles?.indicatorSeparator,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      background: roomStyles.servicePanelElementBackground ?? "#e9ecef",
      marginRight: "8px"
    }),
    singleValue: (provided, state) => ({
      ...provided,
      ...customStyles?.singleValue,
      color: roomStyles.servicePanelElementPrimaryText ?? "#495057",
      cursor: "pointer"
    })
  }

  return (
    <Wrapper style={{ ...stylesWrapper }}>
      {isError && (
        <ErrorTextContainer roomStyles={roomStyles}>
          {errorMessage}
        </ErrorTextContainer>
      )}
      <Select
        value={selectedOption}
        defaultValue={defaultValue}
        isDisabled={disabled}
        isSearchable={isSearchable}
        styles={styles}
        placeholder={t("Select...")}
        onChange={onChange}
        options={options}
      />
    </Wrapper>
  )
}
